import { i18n } from '../../../app/i18n';
import {
  addMonths,
  dayOfWeekOfEpochDay,
  endOfMonth,
  epochDayOf,
  plainDateFromEpochDay,
  startOfMonth,
} from '../../../core/services/plain-date';

import type { DayRange } from './range';
import type { Tick, TimeScale, Zoom } from '../types';

const MS_PER_DAY = 86_400_000;

/**
 * Column width in px at each zoom, at the 62.5% rem base (1rem = 10px). `day`'s 48 is the
 * width the current implementation hardcodes in 59 places as `48` / `4.8rem` / `336px`.
 */
const PX_PER_DAY: Record<Zoom, number> = {
  day: 48,
  week: 12,
  month: 4,
  quarter: 1.5,
};

/**
 * The ONLY `Date` in this module, and it never touches arithmetic: `Intl` needs one, and it is
 * pinned to UTC so the label it produces is a pure function of the epoch day. Locale comes from
 * i18n rather than the browser, because `app/i18n.ts` pins the app to a language and the old
 * header followed the browser independently of it.
 */
const utcInstantOf = (epochDay: number): Date => new Date(epochDay * MS_PER_DAY);

const formatterCache = new Map<string, Intl.DateTimeFormat>();

const formatter = (options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat => {
  const key = `${i18n.language}:${JSON.stringify(options)}`;
  const cached = formatterCache.get(key);
  if (cached) return cached;

  const created = new Intl.DateTimeFormat(i18n.language, { ...options, timeZone: 'UTC' });
  formatterCache.set(key, created);

  return created;
};

const label = (epochDay: number, options: Intl.DateTimeFormatOptions): string =>
  formatter(options).format(utcInstantOf(epochDay));

const dayNumber = (epochDay: number): string =>
  String(Number(plainDateFromEpochDay(epochDay).slice(8, 10)));

const monthBand = (epochDay: number): string => label(epochDay, { month: 'long', year: 'numeric' });

const yearOf = (epochDay: number): string => plainDateFromEpochDay(epochDay).slice(0, 4);

/** Monday-aligned start of the week containing `epochDay`. `dayOfWeek` is 0 = Sunday. */
const startOfWeek = (epochDay: number): number => {
  const dow = dayOfWeekOfEpochDay(epochDay);

  return epochDay - (dow === 0 ? 6 : dow - 1);
};

const startOfQuarter = (epochDay: number): number => {
  const plain = plainDateFromEpochDay(epochDay);
  const month = Number(plain.slice(5, 7));
  const firstMonthOfQuarter = month - ((month - 1) % 3);

  return epochDayOf(startOfMonth(addMonths(plain, firstMonthOfQuarter - month)));
};

type Segment = { day: number; days: number; label: string; groupLabel: string };

const daySegments = (fromDay: number, toDay: number): Segment[] => {
  const segments: Segment[] = [];

  for (let day = fromDay; day <= toDay; day += 1) {
    segments.push({ day, days: 1, label: dayNumber(day), groupLabel: monthBand(day) });
  }

  return segments;
};

const weekSegments = (fromDay: number, toDay: number): Segment[] => {
  const segments: Segment[] = [];

  for (let day = startOfWeek(fromDay); day <= toDay; day += 7) {
    segments.push({
      day,
      days: 7,
      label: label(day, { day: 'numeric', month: 'short' }),
      groupLabel: monthBand(day),
    });
  }

  return segments;
};

const monthSegments = (fromDay: number, toDay: number): Segment[] => {
  const segments: Segment[] = [];
  let cursor = startOfMonth(plainDateFromEpochDay(fromDay));

  while (epochDayOf(cursor) <= toDay) {
    const start = epochDayOf(cursor);
    segments.push({
      day: start,
      days: epochDayOf(endOfMonth(cursor)) - start + 1,
      label: label(start, { month: 'long' }),
      groupLabel: yearOf(start),
    });
    cursor = startOfMonth(addMonths(cursor, 1));
  }

  return segments;
};

const quarterSegments = (fromDay: number, toDay: number): Segment[] => {
  const segments: Segment[] = [];
  let start = startOfQuarter(fromDay);

  while (start <= toDay) {
    const plain = plainDateFromEpochDay(start);
    const next = epochDayOf(startOfMonth(addMonths(plain, 3)));
    const quarter = Math.floor((Number(plain.slice(5, 7)) - 1) / 3) + 1;

    segments.push({
      day: start,
      days: next - start,
      label: `Q${quarter}`,
      groupLabel: yearOf(start),
    });
    start = next;
  }

  return segments;
};

const SEGMENTERS: Record<Zoom, (fromDay: number, toDay: number) => Segment[]> = {
  day: daySegments,
  week: weekSegments,
  month: monthSegments,
  quarter: quarterSegments,
};

export const createTimeScale = (zoom: Zoom): TimeScale => {
  const pxPerDay = PX_PER_DAY[zoom];
  // Shared by `xOf` and `ticks`, so a tick and a bar computed from the same `windowStartDay`
  // can never silently disagree on the origin the way a second, re-derived multiplication could.
  const xOf = (day: number, windowStartDay: number): number => (day - windowStartDay) * pxPerDay;

  return {
    zoom,
    pxPerDay,
    xOf,
    dayAt: (x, windowStartDay) => windowStartDay + Math.floor(x / pxPerDay),
    // `|| 0` folds `Math.sign(-1)`'s `-0` result back to `0`: inert for `===`/`+`/comparison,
    // but distinguishable from `0` by `Object.is`, which `toBe` uses.
    snapDays: (dx) => Math.sign(dx) * Math.round(Math.abs(dx) / pxPerDay) || 0,
    ticks: (fromDay, toDay, windowStartDay, today, workingWeek) => {
      if (toDay < fromDay) return [];

      return SEGMENTERS[zoom](fromDay, toDay).map(
        (segment): Tick => ({
          day: segment.day,
          x: xOf(segment.day, windowStartDay),
          width: segment.days * pxPerDay,
          days: segment.days,
          isNonWorking: zoom === 'day' && !workingWeek.includes(dayOfWeekOfEpochDay(segment.day)),
          isToday: today >= segment.day && today < segment.day + segment.days,
          label: segment.label,
          subLabel: zoom === 'day' ? label(segment.day, { weekday: 'short' }) : undefined,
          groupLabel: segment.groupLabel,
        }),
      );
    },
  };
};

export type MonthBand = { day: number; x: number; width: number; label: string };

/**
 * The month band above the ticks, at whatever pxPerDay the caller is rendering.
 *
 * Bands are derived from the whole range rather than the visible window, and there are only ~13
 * of them at the default padding (240 at the 20-year cap), so they are not virtualized. Deriving
 * them from the window instead would clip a band's width to the visible part and put its right
 * border mid-month.
 */
export const monthBands = (range: DayRange, pxPerDay: number): MonthBand[] => {
  const bands: MonthBand[] = [];
  let cursor = startOfMonth(plainDateFromEpochDay(range.startDay));

  while (epochDayOf(cursor) <= range.endDay) {
    const start = epochDayOf(cursor);
    const days = epochDayOf(endOfMonth(cursor)) - start + 1;

    bands.push({
      day: start,
      x: (start - range.startDay) * pxPerDay,
      width: days * pxPerDay,
      label: monthBand(start),
    });

    cursor = startOfMonth(addMonths(cursor, 1));
  }

  return bands;
};
